import React from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import type { ProfileIndicator } from './profileIndicatorTypes'
import {
  getProfileIndicatorGlow,
  getProfileIndicatorGradient,
  getProfileIndicatorStatusColor
} from './profileIndicatorStyles'

export interface ProfileIndicatorRowProps {
  indicator: ProfileIndicator
  /** Accessible label for the info control (only shown when `indicator.tooltip` is set) */
  helpAriaLabel: string
  rowOrder: number
}

export const ProfileIndicatorRow: React.FC<ProfileIndicatorRowProps> = ({ indicator, helpAriaLabel, rowOrder }) => {
  const statusColor = getProfileIndicatorStatusColor(indicator.level)
  const showHelp = Boolean(indicator.tooltip)

  const labelTypographySx = {
    ...INTERAX_TYPOGRAPHY.body2,
    fontSize: { xs: '11px', sm: '12px', md: '12px' },
    lineHeight: { xs: '14px', sm: '16px', md: '16px' },
    color: INTERAX_COLORS.offWhiteText,
    minWidth: 0,
    overflowWrap: 'break-word' as const
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        px: { xs: 1.25, sm: 1.5, md: 2.25 },
        py: { xs: 1.25, sm: 1.5, md: 1.5 },
        gap: { xs: 1, sm: 1.25, md: 1.5 },
        width: '100%',
        minHeight: { xs: '56px', sm: '58px', md: '60px' },
        background: INTERAX_COLORS.offBlack80,
        boxShadow: 'inset 2px 2px 5px rgba(255, 255, 255, 0.25)',
        borderRadius: '40px',
        flex: 'none',
        order: rowOrder,
        alignSelf: 'stretch',
        flexGrow: 0,
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: showHelp
            ? { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1fr) auto', md: 'minmax(0, 1fr) auto' }
            : 'minmax(0, 1fr)',
          gridTemplateRows: 'auto auto',
          columnGap: { xs: 0.5, sm: 0.75, md: 1 },
          rowGap: { xs: 0.25, sm: 0.5, md: 0.5 },
          flex: 1,
          minWidth: 0,
          alignItems: 'start'
        }}
      >
        <Typography sx={{ ...labelTypographySx, gridColumn: 1, gridRow: 1 }}>{indicator.label}</Typography>

        {showHelp ? (
          <Box
            sx={{
              gridColumn: 2,
              gridRow: '1 / 3',
              alignSelf: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 0
            }}
          >
            <Tooltip
              title={indicator.tooltip}
              arrow
              describeChild
              placement="top-start"
              enterNextDelay={400}
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: { xs: 230, sm: 268, md: 300 },
                    bgcolor: INTERAX_COLORS.heritageBlue,
                    color: INTERAX_COLORS.white,
                    border: `1px solid ${INTERAX_COLORS.white10}`,
                    borderRadius: 1,
                    px: { xs: 1.25, sm: 1.5, md: 1.5 },
                    py: { xs: 1, sm: 1.125, md: 1.125 },
                    boxShadow: '0px 10px 28px rgba(0, 0, 0, 0.45)',
                    ...INTERAX_TYPOGRAPHY.body2,
                    fontSize: { xs: '11px', sm: '12px', md: '12px' },
                    lineHeight: { xs: '15px', sm: '16px', md: '17px' },
                    textAlign: 'left'
                  }
                },
                arrow: {
                  sx: { color: INTERAX_COLORS.heritageBlue }
                },
                popper: {
                  sx: {
                    zIndex: (theme) => theme.zIndex.tooltip + 4
                  }
                }
              }}
            >
              <IconButton
                aria-label={helpAriaLabel}
                size="small"
                sx={{
                  color: INTERAX_COLORS.sun,
                  flexShrink: 0,
                  p: { xs: 0.125, sm: 0.25, md: 0.25 },
                  '&:focus-visible': {
                    outline: `2px solid ${INTERAX_COLORS.sun}`,
                    outlineOffset: 2
                  }
                }}
              >
                <InformationCircleIcon aria-hidden width={18} height={18} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        <Typography
          sx={{
            ...labelTypographySx,
            gridColumn: 1,
            gridRow: 2,
            color: statusColor,
            width: '100%'
          }}
        >
          {indicator.status}
        </Typography>
      </Box>

      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: '32px',
          height: '32px',
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '32px',
            height: '32px',
            background: 'rgba(33, 38, 41, 0.4)',
            boxShadow: 'inset 2px 2px 3px rgba(0, 0, 0, 0.5)',
            borderRadius: '50%'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: '4px',
            top: '4px',
            width: '24px',
            height: '24px',
            background: getProfileIndicatorGradient(indicator.level),
            border: `1px solid ${INTERAX_COLORS.white25}`,
            boxShadow: getProfileIndicatorGlow(indicator.level),
            borderRadius: '50%'
          }}
        />
      </Box>
    </Box>
  )
}
